"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

/**
 * Below the `narrow` breakpoint the header nav and search are hidden; this
 * disclosure menu is the only way to reach them on a phone.
 */
export function MobileMenu({ links }: { links: [string, string][] }) {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // Route change means a link was followed — the menu's job is done. Adjusted
  // during render (not an effect): the repo's lint forbids setState-in-effect.
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    if (open) setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="narrow:hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? "Close menu" : "Open menu"}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-paper text-ink-3 hover:text-copper"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
          {open ? (
            <path d="M4 4l10 10M14 4L4 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          ) : (
            <path d="M2.5 5h13M2.5 9h13M2.5 13h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          )}
        </svg>
      </button>

      {open && (
        <div className="absolute inset-x-0 top-16 z-40 border-b border-border bg-surface shadow-[0_12px_24px_-12px_rgba(31,41,26,.25)]">
          <form action="/search" className="px-[var(--gutter)] pt-4">
            <div className="flex items-center gap-2 rounded-full border border-border bg-paper px-4 py-2.5">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0 text-muted-2" aria-hidden>
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                name="q"
                placeholder="Search rule books…"
                className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-muted-2"
                aria-label="Search rule books"
              />
            </div>
          </form>
          <nav className="flex flex-col px-[var(--gutter)] py-3">
            {links.map(([label, href]) => (
              <Link
                key={href}
                href={href}
                className="border-b border-border-soft py-3.5 text-[15px] font-medium text-ink-3 last:border-b-0 hover:text-copper"
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}
